import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import html2pdf from "html2pdf.js/src/index.js";
import Backdrop from "../../../UI/Backdrop/Backdrop";
import Chart from "../../../common/Chart/Chart";
import Map from "../../../common/Map/Map";

import ClamCloudMl from "../../AnalysisReports/ClamCloudMlResults/ClamCloudMl";
import APKInformationCard from "../../AnalysisReports/APKInformationCard/APKInformationCard";
import AndroidPluginsFlat from "../../AnalysisReports/AndroidPlugins/AndroidPluginsFlat";
import IocsFlat from "../../AnalysisReports/MemoryBlock/Tabs/IOCs/IocsFlat";

import CrimesFlat from "../../../../Pages/AnalysisReports/AndroidAnalysisReports/Content/Tabs/Crimes/CrimesFlat";
import StaticCodeIssuesFlat from "../../../../Pages/AnalysisReports/AndroidAnalysisReports/Content/Tabs/StaticCodeIssues/StaticCodeIssuesFlat";
import PermissionsFlat from "../../../../Pages/AnalysisReports/AndroidAnalysisReports/Content/Tabs/Permissions/PermissionsFlat";
import Matrix from "../../../../Pages/AnalysisReports/Content/Tabs/Matrix/Matrix";
import StaticInfo from "../../../../Pages/AnalysisReports/Content/Tabs/StaticInfo/StaticInfo";
import SnortSuricata from "../../../../Pages/AnalysisReports/Content/Tabs/SnortSuricata/SnortSuricata";

import {
  RequestApkInfo,
  RequestCrimes,
  RequestPermissions,
  RequestPlugins,
  RequestStaticCodeIssues,
} from "../../../../store/actions/androidActions";
import {
  RequestFilesInfo,
  RequestGeneralAnalysis,
  RequestGetReportLogo,
  RequestIocs,
  RequestNetwork,
} from "../../../../store/actions/analysisReportActions";
import {
  getAndroidReportTime,
  getAndroidVerdict,
} from "../../../../Pages/AndroidReportExport/utils/dataSanitization";
import { MA360_REPORT_LOGO } from "../../../../utils/constants/reportLogos";

import "./styles.scss";

//GET ANDROID REPORT ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// Section flag -> the block that gets dropped from the dom before capture
const SECTION_SELECTORS = {
  g: ".android-reportpdf-main__content--general",
  k: ".android-reportpdf-main__content--apk",
  c: ".android-reportpdf-main__content--crimes",
  i: ".android-reportpdf-main__content--static-code-issues",
  p: ".android-reportpdf-main__content--permissions",
  m: ".android-reportpdf-main__content--mitre",
  r: ".android-reportpdf-main__content--static-results",
  n: ".android-reportpdf-main__content--network",
  w: ".android-reportpdf-main__content--network-alerts",
  u: ".android-reportpdf-main__content--plugins",
  o: ".android-reportpdf-main__content--iocs",
};

const AndroidReportPDF = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const middleRef = useRef(null);
  let query = useQuery();

  const originalID = query.get("data");
  const stateParam = query.get("state");

  const general = useSelector((state) => state.analysisReports.general);
  const filesInfo = useSelector((state) => state.analysisReports.filesInfo);
  const iocs = useSelector((state) => state.analysisReports.iocs);
  const mitre = useSelector((state) => state.analysisReports.mitre);
  const staticInfoList = useSelector((state) => state.analysisReports.staticInfoList);
  const reportLogo = useSelector((state) => state.analysisReports.reportLogo);

  const apkInfo = useSelector((state) => state.android.apkInfo);
  const crimes = useSelector((state) => state.android.crimes);
  const staticCodeIssues = useSelector((state) => state.android.staticCodeIssues);
  const permissions = useSelector((state) => state.android.permissions);
  const plugins = useSelector((state) => state.android.plugins);

  const [currentDate] = useState(new Date());

  useEffect(() => {
    dispatch(RequestGeneralAnalysis({ parentID: originalID, clickedFileID: originalID }));
    dispatch(RequestNetwork({ parentID: originalID, clickedFileID: originalID }));
    dispatch(RequestIocs({ parentID: originalID, clickedFileID: originalID }));
    dispatch(RequestFilesInfo(originalID));
    dispatch(RequestApkInfo(originalID));
    dispatch(RequestCrimes(originalID));
    dispatch(RequestStaticCodeIssues(originalID));
    dispatch(RequestPermissions(originalID));
    dispatch(RequestPlugins(originalID));
    dispatch(RequestGetReportLogo());
  }, [dispatch, originalID]);

  const generatePdf = () => {
    const reportContainer = document.querySelector(".android-reportpdf-main__content");

    Object.entries(SECTION_SELECTORS).forEach(([flag, selector]) => {
      if (query.get(flag) === "false") {
        reportContainer.querySelectorAll(selector).forEach((unwantedDiv) => {
          unwantedDiv.remove();
        });
      }
    });

    const fileName = general?.file_name || "Android";

    const options = {
      margin: 10,
      filename: "Report - " + fileName + ".pdf",
      image: { type: "jpeg", quality: 0.5 },
      html2canvas: { dpi: 75, scale: 2, letterRendering: true },
      jsPDF: { unit: "mm", format: "a3", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    const pdfPromise = new Promise((resolve, reject) => {
      html2pdf(reportContainer, options)
        .then(() => resolve())
        .catch((error) => {
          console.error("Error during PDF generation:", error);
          reject(error);
        });
    });

    pdfPromise.then(() => {
      history.push(`/android-report-export?data=${originalID}&state=${stateParam}`);
    });
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "visible";
    };
  }, []);

  useEffect(() => {
    if (middleRef.current) {
      const middleElement = middleRef.current;
      const middleElementTop = middleElement.offsetTop;
      const middleElementHeight = middleElement.clientHeight;
      const windowHeight = window.innerHeight;

      const scrollTo = middleElementTop - (windowHeight - middleElementHeight) / 2;
      window.scrollTo(0, scrollTo);
    }

    // The map and the charts need a moment to draw into their canvases before
    // html2canvas rasterizes them. Unlike the ThreatHunter report there is no
    // single aggregate request here: ten slices arrive independently and
    // Matrix/StaticInfo fire two more after mount, so every one of them is a
    // dependency. The effect clears the timer on each re-run, which means the
    // capture happens once the last slice has settled.
    const timeoutId = setTimeout(() => {
      if (general && Object.keys(general).length > 0) {
        generatePdf();
      }
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [
    general,
    apkInfo,
    crimes,
    staticCodeIssues,
    permissions,
    plugins,
    iocs,
    mitre,
    staticInfoList,
    reportLogo,
  ]);

  const targetFile = (Array.isArray(filesInfo) ? filesInfo : [])[0]?.target?.file || {};

  return (
    <div className="android-reportpdf">
      <div className="android-reportpdf-loading">
        <Backdrop absolute={true} show={true} message="Generating PDF. Please wait..." />
      </div>
      <div className="android-reportpdf-main">
        <div ref={middleRef} className="android-reportpdf-main__content">
          <p className="android-reportpdf-main__content--date">
            {currentDate.toLocaleDateString("tr-TR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
            -cyberfortress©
          </p>
          <img
            alt="Report logo"
            className="android-reportpdf-main__content--img"
            src={reportLogo ? "data:image/png;base64," + reportLogo : MA360_REPORT_LOGO}
          />
          <p className="android-reportpdf-main__content--header">Android Analysis Report</p>
          <p className="android-reportpdf-main__content--subheader">
            {general?.file_name || "Unknown file"} — {getAndroidReportTime(general)} —{" "}
            {getAndroidVerdict(general, stateParam)}
          </p>

          <div className="android-reportpdf-main__content--general">
            <h2>General</h2>
            <div className="android-reportpdf-main__content--general-content">
              <table>
                <tbody>
                  <tr>
                    <td>Target</td>
                    <td>: {general?.file_name}</td>
                  </tr>
                  <tr>
                    <td>Size</td>
                    <td>: {general?.size}</td>
                  </tr>
                  <tr>
                    <td>MD5</td>
                    <td>: {targetFile.md5}</td>
                  </tr>
                  <tr>
                    <td>SHA1</td>
                    <td>: {targetFile.sha1}</td>
                  </tr>
                  <tr>
                    <td>SHA256</td>
                    <td>: {general?.sha1}</td>
                  </tr>
                  <tr>
                    <td>SSDEEP</td>
                    <td>: {targetFile.ssdeep}</td>
                  </tr>
                </tbody>
              </table>
              <div className="android-reportpdf-main__content--general-content_score">
                <div className="android-reportpdf-main__content--general-content_score-verdict">
                  <span>{getAndroidVerdict(general, stateParam)}</span>
                </div>
                <div className="android-reportpdf-main__content--general-content_score-chart analysis-reports__header-doughnutChart">
                  <Chart
                    name="doughnut-dark"
                    type="doughnut"
                    fileState={stateParam}
                    decision_score={general?.decision_score || "-"}
                  />
                </div>
                <ClamCloudMl report={true} />
              </div>
            </div>
          </div>

          <div className="android-reportpdf-main__content--apk">
            <h2>APK Information</h2>
            <div className="android-reportpdf-main__content--apk-content">
              <APKInformationCard report={true} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--crimes">
            <h2>Possible Malicious Actions</h2>
            <div className="android-reportpdf-main__content--crimes-content">
              <CrimesFlat data={crimes} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--static-code-issues">
            <h2>Static Code Issues</h2>
            <div className="android-reportpdf-main__content--static-code-issues-content">
              <StaticCodeIssuesFlat data={staticCodeIssues} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--permissions">
            <h2>Permissions</h2>
            <div className="android-reportpdf-main__content--permissions-content">
              <PermissionsFlat data={permissions} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--mitre">
            <h2>MITRE ATT&CK™ Matrix</h2>
            <div className="android-reportpdf-main__content--mitre-content">
              <Matrix report={true} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--static-results">
            <h2>Static Results</h2>
            <div className="android-reportpdf-main__content--static-results-content">
              <StaticInfo report={true} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--network">
            <h2>Network Activity</h2>
            <div className="android-reportpdf-main__content--network-content">
              {/* reportPdf drops the zoom controls and the country panel, which
                  would only be dead pixels once rasterized */}
              <Map reportPdf={true} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--network-alerts">
            <h2>Network Alerts</h2>
            <div className="android-reportpdf-main__content--network-alerts-content">
              <SnortSuricata report={true} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--plugins">
            <h2>Plugins</h2>
            <div className="android-reportpdf-main__content--plugins-content">
              <AndroidPluginsFlat plugins={plugins} />
            </div>
          </div>

          <div className="android-reportpdf-main__content--iocs">
            <h2>IOCs</h2>
            <div className="android-reportpdf-main__content--iocs-content">
              <IocsFlat iocs={iocs} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AndroidReportPDF;
